/**
 * Individual estimated tax: who collects it, when each installment is due, and the safe harbor
 * (the least a taxpayer can pay through withholding and estimates to avoid an underpayment
 * penalty). Each rule names its source and when it was last reviewed; review every year in Q4.
 */
import { nextBusinessDay } from "./due-dates";

export type EstimateAuthority = {
  code: string;
  name: string;
  form: string;
  /** Month and day of each installment for a calendar-year individual; the 4th is in the next year. */
  installments: Array<[number, number]>;
  /** Percent of the current year's tax that satisfies the safe harbor. */
  currentYearPct: number;
  /** Percent of the prior year's tax; federal uses 110% above an AGI threshold. */
  priorYearPct: number;
  priorYearPctHighIncome?: { pct: number; agiOverCents: number; agiOverCentsMfs: number };
  /** No penalty when the balance due after withholding is under this. */
  thresholdCents: number;
  payUrl: string;
  payLabel: string;
  source: string;
  reviewedOn: string;
};

export const ESTIMATE_AUTHORITIES: Record<string, EstimateAuthority> = {
  federal: {
    code: "federal",
    name: "IRS",
    form: "1040-ES",
    installments: [
      [4, 15],
      [6, 15],
      [9, 15],
      [1, 15],
    ],
    currentYearPct: 90,
    priorYearPct: 100,
    priorYearPctHighIncome: { pct: 110, agiOverCents: 15_000_000, agiOverCentsMfs: 7_500_000 },
    thresholdCents: 100_000,
    payUrl: "https://www.irs.gov/payments/direct-pay",
    payLabel: "Pay with IRS Direct Pay",
    source: "IRC §6654(d); IRS Form 1040-ES instructions",
    reviewedOn: "2026-10-18",
  },
  MD: {
    code: "MD",
    name: "Comptroller of Maryland",
    form: "502D",
    installments: [
      [4, 15],
      [6, 15],
      [9, 15],
      [1, 15],
    ],
    currentYearPct: 90,
    priorYearPct: 110,
    thresholdCents: 50_000,
    payUrl: "https://interactive.marylandtaxes.gov/Individuals/iFile_ChooseForm/default.asp",
    payLabel: "Pay with Maryland iFile (Personal Estimated Payment)",
    source: "COMAR 03.04.01.02; Maryland Form 502UP instructions",
    reviewedOn: "2026-10-18",
  },
  VA: {
    code: "VA",
    name: "Virginia Tax",
    form: "760ES",
    installments: [
      [5, 1],
      [6, 15],
      [9, 15],
      [1, 15],
    ],
    currentYearPct: 90,
    priorYearPct: 100,
    thresholdCents: 15_000,
    payUrl: "https://www.tax.virginia.gov/individual-estimated-tax-payments",
    payLabel: "Pay with Virginia Tax online services",
    source: "Virginia Form 760ES and 760C instructions",
    reviewedOn: "2026-10-18",
  },
  DC: {
    code: "DC",
    name: "DC Office of Tax and Revenue",
    form: "D-40ES",
    installments: [
      [4, 15],
      [6, 15],
      [9, 15],
      [1, 15],
    ],
    currentYearPct: 90,
    priorYearPct: 110,
    thresholdCents: 10_000,
    payUrl: "https://mytax.dc.gov/",
    payLabel: "Pay with MyTax.DC.gov",
    source: "DC OTR: Underpayment of estimated tax interest (Form D-2210)",
    reviewedOn: "2026-10-18",
  },
};

/**
 * The four installment due dates for a tax year, moved to the next business day. A legal holiday
 * observed in the District of Columbia (Emancipation Day) moves federal dates (IRC §7503, Notice
 * 2011-17) and DC's own; Maryland's rule moves its dates only for weekends and Maryland legal
 * holidays, and Virginia's (Form 760ES) for weekends and holidays, so neither moves for
 * Emancipation Day. A state's own holidays beyond the federal ones come from the firm (Firm Settings ›
 * Rules and rates): `stateHolidays` gives the list in effect for a statutory date (Maryland and
 * Virginia only; none until staff add them).
 */
export function estimateDueDates(code: string, taxYear: number, stateHolidays?: (statutoryDate: string) => readonly string[] | null): string[] {
  const a = ESTIMATE_AUTHORITIES[code];
  if (!a) throw new Error(`No estimated tax rules for ${code}`);
  const irs = code === "federal" || code === "DC";
  const own = code === "MD" || code === "VA" ? stateHolidays : undefined;
  return a.installments.map(([m, d], i) => {
    const raw = `${i === 3 ? taxYear + 1 : taxYear}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    return nextBusinessDay(raw, { irs, extraHolidays: own?.(raw) });
  });
}

export type SafeHarborInput = {
  priorYearTaxCents: number | null;
  priorYearAgiCents: number | null;
  /** Married filing separately uses the lower federal AGI threshold. */
  marriedFilingSeparately?: boolean;
  /** The prior year was a full 12-month year (a short year can't be used for the prior-year test). */
  priorYearFullYear?: boolean;
  projectedTaxCents: number | null;
  withholdingCents: number;
};

export type SafeHarbor = {
  /** The least the year's withholding and estimates together must reach. */
  requiredAnnualCents: number;
  basis: "prior_year" | "current_year";
  priorYearPct: number | null;
  /** What remains after withholding, split into four equal installments (any odd cents in the last). */
  estimatesCents: number;
  installments: [number, number, number, number];
  /** True when the balance after withholding is under the authority's penalty threshold. */
  underThreshold: boolean;
};

/**
 * The safe harbor: the lesser of the current-year percent of projected tax and the prior-year
 * percent of last year's tax. Either figure may be missing; with neither there is no answer.
 */
export function safeHarbor(code: string, x: SafeHarborInput): SafeHarbor | null {
  const a = ESTIMATE_AUTHORITIES[code];
  if (!a) throw new Error(`No estimated tax rules for ${code}`);
  let priorPct: number | null = null;
  let prior: number | null = null;
  if (x.priorYearTaxCents !== null && x.priorYearFullYear !== false) {
    priorPct = a.priorYearPct;
    const hi = a.priorYearPctHighIncome;
    if (hi && x.priorYearAgiCents !== null && x.priorYearAgiCents > (x.marriedFilingSeparately ? hi.agiOverCentsMfs : hi.agiOverCents)) priorPct = hi.pct;
    prior = Math.ceil((x.priorYearTaxCents * priorPct) / 100);
  }
  const current = x.projectedTaxCents !== null ? Math.ceil((x.projectedTaxCents * a.currentYearPct) / 100) : null;
  if (prior === null && current === null) return null;
  const usePrior = prior !== null && (current === null || prior <= current);
  const required = usePrior ? prior! : current!;
  const estimates = Math.max(0, required - x.withholdingCents);
  // Whole dollars per installment, the way vouchers are written; the last absorbs the remainder.
  const each = Math.floor(estimates / 4 / 100) * 100;
  const installments: [number, number, number, number] = [each, each, each, estimates - 3 * each];
  const balance = Math.max(0, (x.projectedTaxCents ?? required) - x.withholdingCents);
  return { requiredAnnualCents: required, basis: usePrior ? "prior_year" : "current_year", priorYearPct: usePrior ? priorPct : null, estimatesCents: estimates, installments, underThreshold: balance < a.thresholdCents };
}
